import React from "react";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Typography,
  Table,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  TableBody,
  CircularProgress,
} from "@material-ui/core";
import { useForm, FormContext, FieldError } from "react-hook-form";
import { IPennsylvaniaStateBoardReportProgramCode } from "../../../interfaces/setup/reports/IPennsylvaniaStateBoardReportProgramCode";
import InputField from "../../../components/_Layout/Inputs/InputField";
import { InputType } from "../../../constants/uiConstants/inputConstants";
import { cloneDeep } from "lodash";
import { getPennsylvaniaStateBoardReportProgramCode, upsertPennsylvaniaStateBoardReportProgramCode } from "../../../api/reports/stateBoard/stateBoardReportApi";
import ProgressSaveButton from "../../../components/_Layout/Buttons/ProgressSaveButton";
import CustomSnackbar from "../../../components/notifications/CustomSnackbar";
import { CustomSnackBarProps } from "../../../interfaces/common/CustomSnackBarProps";


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      minHeight: "100%",
      padding: theme.spacing(3),
      overflowX: "hidden",
      overflowY: "auto",
    },
    container: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      height: "100%",
    },
    details: {
      padding: theme.spacing(2, 0.5),
      flex: "1 1",
    },
    componentActions: {
      marginTop: theme.spacing(3),
    },
    wrapper: {
      height: "80%",
      padding: "3px",
      overflow:'auto',
    },
    form: {
      height: "100%",
    },
    titleFont: {
      background: "	rgb(220,220,220)",
      padding: "15px",
      textAlign: "left",
      fontFamily: "sans-serif",
    },
    headerFont: {
      background: "	rgb(243,240,239)",
    },
  })
);

interface IProps {
  stateId: string;
  stateDescription: string;
  stateBoardReportId: string;
  stateBoardReportDescription: string;
  campusId: string;
}

const PennsylvaniaStateBoardReportSetup = (props: IProps) => {
  const classes = useStyles({});
  const data = useForm<any>({
    mode: "onBlur",
  });

  const [rows, setRows] = React.useState<IPennsylvaniaStateBoardReportProgramCode[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [snackBarProps, setSnackBarProps] = React.useState<CustomSnackBarProps>(
      {
          showSnackBar: false,
          messageInfo: undefined,
          variant: "info",
      }
  );

  const handleProgramCodeChange = (value:any, programId:string) =>{
    let copy = cloneDeep(rows);
    let obj = copy.find(m => m.programId == programId);

    if(!!obj){
      obj.programCode = value;
    }

    setRows(copy);
  }

  const getSettings = () => {
    if(!!props.stateBoardReportId && !!props.campusId){
      setIsLoading(true);
      getPennsylvaniaStateBoardReportProgramCode(props.stateBoardReportId, props.campusId.toLowerCase() == 'all' ? null : props.campusId)
      .then(res => {
        setIsLoading(false);
        setRows(res);
      }).catch( e=> {
        setIsLoading(false);
      });
    }else{
      setRows([]);
      setIsLoading(false);
    }
  }

  const handleSubmit = () =>{
    if(!!rows && rows.length > 0){
      setIsSaving(true);
      upsertPennsylvaniaStateBoardReportProgramCode(props.stateBoardReportId, cloneDeep(rows))
      .then(res => {
        setIsSaving(false);
        setSnackBarProps((props: any) => {
          return {
              variant: "success",
              showSnackBar: true,
              messageInfo: "Saved successfully!",
          };
      });
      })
      .catch(e => {
        setSnackBarProps((props: any) => {
          return {
              variant: "error",
              showSnackBar: true,
              messageInfo: "Failed to save!",
          };
      });
      });
    }
  }

  React.useEffect(() => {
    getSettings();
  },[props.stateBoardReportId, props.campusId]);

  return (
          <>
          <CustomSnackbar
                variant={snackBarProps.variant}
                message={snackBarProps.messageInfo}
                open={snackBarProps.showSnackBar}
                onClose={(event?: React.SyntheticEvent, reason?: string) => {
                    setSnackBarProps((props: any) => {
                        return { ...props, showSnackBar: false };
                    });
                }}
            ></CustomSnackbar>
          <div className={classes.wrapper}>
                <Table
                  //aria-labelledby="tableTitle"
                  //size="medium"
                  aria-label="sticky table"
                  stickyHeader
                >
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headerFont} style={{width:'60%'}}>Program Name</TableCell>
                      <TableCell className={classes.headerFont}>Program Code</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!!rows && !isLoading && rows.length > 0 && rows.map(row => <TableRow key={`${row.programId}`}>
                      <TableCell>{row.programDescription}</TableCell>
                      <TableCell>
                      <InputField
												type={InputType.TEXT}
												key={`txt${row.programId}}`}
												id={`txt_${row.programId}`}
												label="Program Code"
												name="programCode"
												defaultValue={row?.programCode}
												onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
													handleProgramCodeChange(e.target.value, row.programId);
												}}
											/>
                      </TableCell>
                    </TableRow>)}
                    {isLoading && <TableRow>
                      <TableCell style={{textAlign:'center'}} colSpan={2}>
                        <CircularProgress />
                      </TableCell>
                      </TableRow>}
                  </TableBody>
                </Table>
          </div>
          <div className={classes.componentActions}>
          <ProgressSaveButton
																	key="btnSavePennsylvaniaProgramCodeSettings"
																	text="Save"
																	onClick={handleSubmit}
																	loading={isSaving}
																	disabled={!rows || rows.length <= 0}
																	size="small"
																></ProgressSaveButton>
          </div></>
  );
};

export default PennsylvaniaStateBoardReportSetup;
